/**
 * A list through Excel: export, edit there, import back. Employees, sites, vehicles and
 * the company's documents.
 *
 * The import shows exactly what will change before anything does — per person, old
 * value to new — and the rows it cannot take, with the reason. "Apply" then sends the
 * same file again; the server re-checks it against the data as it is at that moment.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FileDrop } from '@/components/FileDrop';
import { Modal } from '@/components/Modal';
import { useDir, useI18n, type TranslationKey } from '@/i18n';
import { api, ApiError, type SheetImport, type SheetKind } from '@/lib/api';
import { dateShort } from '@/lib/format';

/** What each list calls itself, and what to refresh once its sheet is applied. */
const SHEETS: Record<SheetKind, { title: TranslationKey; newRow: TranslationKey; refresh: string[] }> = {
  employees: {
    title: 'sheet.importTitle',
    newRow: 'sheet.new',
    refresh: ['users', 'user', 'worker-register', 'compliance'],
  },
  sites: { title: 'sheet.importSitesTitle', newRow: 'sheet.newSite', refresh: ['sites', 'site', 'clients'] },
  vehicles: { title: 'sheet.importVehiclesTitle', newRow: 'sheet.newVehicle', refresh: ['vehicles', 'vehicle', 'alerts'] },
  company: {
    title: 'sheet.importCompanyTitle',
    newRow: 'sheet.newDocument',
    refresh: ['company-register', 'compliance', 'alerts', 'document-types'],
  },
};

/** The file as base64, for a JSON body. */
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Dates read as dates, everything else as itself; an empty value says so. */
function shown(value: string | null, empty: string): string {
  if (value === null || value === '') return empty;
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? dateShort(value) : value;
}

export function ExportSheetButton({ kind }: { kind: SheetKind }) {
  const { t } = useI18n();
  const download = useMutation({
    mutationFn: async () => {
      const { blob, filename } = await api.downloadSheet(kind);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
  return (
    <button
      type="button"
      className="btn-secondary"
      disabled={download.isPending}
      title={download.error instanceof ApiError ? download.error.message : undefined}
      onClick={() => download.mutate()}
    >
      {download.isPending ? t('sheet.exporting') : t('sheet.export')}
    </button>
  );
}

export function ImportSheetDialog({ kind, open, onClose }: { kind: SheetKind; open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { forward } = useDir();
  const sheet = SHEETS[kind];
  const queryClient = useQueryClient();
  const [file, setFile] = useState<{ name: string; base64: string } | null>(null);
  const [result, setResult] = useState<SheetImport | null>(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    check.reset();
    apply.reset();
  };

  const check = useMutation({
    mutationFn: async (picked: File) => {
      const base64 = await toBase64(picked);
      setFile({ name: picked.name, base64 });
      return api.importSheet(kind, base64, false);
    },
    onSuccess: setResult,
  });

  const apply = useMutation({
    mutationFn: () => api.importSheet(kind, file!.base64, true),
    onSuccess: async (r) => {
      setResult(r);
      for (const key of sheet.refresh) await queryClient.invalidateQueries({ queryKey: [key] });
    },
  });

  const s = result?.summary;
  const toApply = s ? s.update + s.create : 0;
  const busy = check.isPending || apply.isPending;
  const error = check.error ?? apply.error;

  return (
    <Modal
      open={open}
      onClose={() => {
        if (busy) return;
        reset();
        onClose();
      }}
      size="lg"
      title={t(sheet.title)}
      description={result?.applied ? undefined : t('sheet.importHint')}
      footer={
        result?.applied ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t('common.close')}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={!result || toApply === 0 || busy}
              onClick={() => apply.mutate()}
            >
              {apply.isPending ? t('common.saving') : t('sheet.apply', { count: toApply })}
            </button>
            <button type="button" className="btn-secondary" disabled={busy} onClick={reset}>
              {t('sheet.another')}
            </button>
          </>
        )
      }
    >
      <div className="space-y-4">
        {!file ? (
          <FileDrop onPick={(f) => check.mutate(f)} busy={check.isPending} label={t('sheet.drop')} />
        ) : (
          <p className="text-sm text-muted">{file.name}</p>
        )}

        {check.isPending ? <p className="text-sm text-muted">{t('sheet.checking')}</p> : null}

        {s ? (
          <div className="flex flex-wrap gap-2 text-sm">
            {result?.applied ? (
              <span className="rounded-full bg-success-soft px-3 py-1 font-semibold text-success">
                {t('sheet.done', { count: s.update + s.create })}
              </span>
            ) : null}
            <span className="rounded-full bg-surface-alt px-3 py-1">{t('sheet.updates', { count: s.update })}</span>
            <span className="rounded-full bg-surface-alt px-3 py-1">{t('sheet.creates', { count: s.create })}</span>
            <span className="rounded-full bg-surface-alt px-3 py-1 text-muted">{t('sheet.unchanged', { count: s.unchanged })}</span>
            {s.error > 0 ? (
              <span className="rounded-full bg-danger-soft px-3 py-1 font-semibold text-danger">
                {t('sheet.errors', { count: s.error })}
              </span>
            ) : null}
          </div>
        ) : null}

        {result && result.rows.length > 0 ? (
          <ul className="max-h-[50vh] overflow-y-auto rounded border border-border divide-y divide-border">
            {result.rows.map((r) => (
              <li key={r.row} className="px-3 py-2.5 text-sm">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold">{r.name}</span>
                  <span className="text-xs text-subtle">
                    {t('sheet.row', { row: r.row })} ·{' '}
                    {r.action === 'create'
                      ? t(sheet.newRow)
                      : r.action === 'error'
                        ? t('sheet.skipped')
                        : t('sheet.changed', { count: r.changes.length })}
                  </span>
                </div>
                {r.errors.length > 0 ? (
                  <ul className="mt-1 space-y-0.5">
                    {r.errors.map((e) => (
                      <li key={e} className="text-xs text-danger">
                        {e}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {r.notes && r.notes.length > 0 && r.action !== 'error' ? (
                  <ul className="mt-1 space-y-0.5">
                    {r.notes.map((n) => (
                      <li key={n} className="text-xs text-primary">
                        {n}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {r.changes.length > 0 && r.action !== 'error' ? (
                  <ul className="mt-1 space-y-0.5">
                    {r.changes.map((ch) => (
                      <li key={ch.key} className="text-xs text-muted">
                        <span className="font-medium text-text">{ch.label}:</span>{' '}
                        {r.action === 'create' ? null : (
                          <>
                            <span className="line-through">{shown(ch.from, t('sheet.empty'))}</span>
                            {` ${forward} `}
                          </>
                        )}
                        <span className="text-text">{shown(ch.to, t('sheet.empty'))}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        ) : result && !result.applied && s && s.error === 0 && toApply === 0 ? (
          <p className="text-sm text-muted">{t('sheet.nothing')}</p>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error instanceof ApiError ? error.message : t('common.unknownError')}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
